import { FilterOps, GQL_ID, Product, ProductReview } from '@@types'
import { useTranslation } from 'react-i18next'

import { useFetchProductReviews } from '@entities/Product'
import { OpenReviewCreate } from '@entities/Review'

import { useAuth } from '@features/auth'

import { cn } from '@shared/lib/cn'
import { toSafeDate } from '@shared/uttils'
import RatingPreview from '@shared/ui/rating/preview/preview'

import styles from './ProductReviews.module.scss'

interface ProductReviewsProps {
  product: Product
}

interface SingleReviewProps {
  review: ProductReview
  currentUserId?: GQL_ID | null
}

function formatDate (dateValue: unknown): string {
  const date = toSafeDate(dateValue)
  if (!date) return ''
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

function SingleReview ({ review, currentUserId }: SingleReviewProps) {
  const { t } = useTranslation('product')
  const isMyReview = currentUserId && review.customer_id === currentUserId
  const isModeration = review.status === 0

  return (
    <div className={cn(styles.review, {
      [styles.review_own]: isMyReview
    })}>
      <div className={styles.review__header}>
        <span className={styles.review__author}>
          {review.customer?.first_name || 'Покупатель'}
          {isMyReview && (
            <span className={styles.review__badge}>{t('reviews.my_review')}</span>
          )}
        </span>
        {review.created_at && (
          <span className={styles.review__date}>
            {formatDate(review.created_at)}
          </span>
        )}
      </div>
      {isModeration && (
        <div className={styles.review__moderation}>
          {t('reviews.moderation')}
        </div>
      )}
      <div className={styles.review__rating}>
        <RatingPreview value={review.rating} />
      </div>
      {review.comment && (
        <p className={styles.review__comment}>{review.comment}</p>
      )}
    </div>
  )
}

export default function ProductReviews ({ product }: ProductReviewsProps) {
  const { t } = useTranslation('product')
  const { user } = useAuth()

  const { productReviews, loading, error } = useFetchProductReviews({
    filter: {
      fields: [ 'product_id' ],
      op: FilterOps.EQUAL,
      value: product.id
    },
    pager: {
      limit: 20
    }
  })

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.root__loading}>
          {t('reviews.loading')}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.root__error}>
          {t('reviews.error')}
        </div>
      </div>
    )
  }

  if (!productReviews || productReviews.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.root__empty}>
          {t('reviews.empty')}
        </div>
        <div className={styles.root__action}>
          <OpenReviewCreate data={product} />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.root__action}>
        <OpenReviewCreate data={product} />
      </div>
      <div className={styles.root__list}>
        {productReviews.map((review) => (
          <SingleReview key={review.id} review={review} currentUserId={user?.id} />
        ))}
      </div>
    </div>
  )
}
